import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Copy, Plus, Trash2, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react';
import ImageUploader from '../ImageUploader';
import { resolveImageUrl } from '../../../lib/imageUtils';
import { TRANSFORMATIONS_SECTION_DEFAULTS } from '../../../lib/transformationsSectionDefaults';

const FONT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: "'Cinzel', serif", label: 'Cinzel' },
  { value: "'Playfair Display', serif", label: 'Playfair' },
  { value: "'Lato', sans-serif", label: 'Lato' },
  { value: "'Montserrat', sans-serif", label: 'Montserrat' },
  { value: "'Titillium Web', sans-serif", label: 'Titillium Web' },
  { value: "'Cormorant Garamond', serif", label: 'Cormorant' },
  { value: "'Lora', Georgia, 'Times New Roman', serif", label: 'Lora' },
  { value: "'Poppins', sans-serif", label: 'Poppins' },
  { value: "'Raleway', sans-serif", label: 'Raleway' },
  { value: "'Great Vibes', cursive", label: 'Great Vibes' },
  { value: "'Dancing Script', cursive", label: 'Dancing Script' },
  { value: "'Pacifico', cursive", label: 'Pacifico' },
  { value: "'Sacramento', cursive", label: 'Sacramento' },
  { value: "'Alex Brush', cursive", label: 'Alex Brush' },
  { value: "'Kaushan Script', cursive", label: 'Kaushan Script' },
  { value: "'Satisfy', cursive", label: 'Satisfy' },
  { value: "'Allura', cursive", label: 'Allura' },
  { value: "'Caveat', cursive", label: 'Caveat' },
];
const SIZE_OPTIONS = ['10px','12px','14px','16px','18px','20px','24px','28px','32px','36px','42px','48px'];

/** Sizes for Transformations page body section titles (kicker + main heading); includes responsive presets. */
const SECTION_BODY_FONT_SIZE_OPTIONS = [
  '11px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '28px', '32px', '36px', '42px', '48px',
  'clamp(1.05rem, 2.15vw, 1.2rem)',
  'clamp(1.14rem, 2.2vw, 1.38rem)',
  'clamp(1.2rem, 2.5vw, 1.6rem)',
  'clamp(1.5rem, 3vw, 2rem)',
];

const LINE_HEIGHT_OPTIONS = [
  { value: '', label: 'Line height' },
  { value: '1.55', label: 'Tight' },
  { value: '1.7', label: 'Snug' },
  { value: '1.88', label: 'Default' },
  { value: '2', label: 'Airy' },
  { value: '2.15', label: 'Loose' },
];

const LETTER_SPACING_OPTIONS = [
  { value: '', label: 'Spacing' },
  { value: '0', label: 'None' },
  { value: '0.02em', label: 'Subtle' },
  { value: '0.05em', label: 'Wide' },
  { value: '0.08em', label: 'Wider' },
  { value: '0.12em', label: 'Luxury' },
  { value: '0.2em', label: 'Spread' },
  { value: '0.25em', label: 'Max' },
];

const TEXT_ALIGN_OPTIONS = [
  { value: '', label: 'Align' },
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const TEXT_TRANSFORM_OPTIONS = [
  { value: '', label: 'Case' },
  { value: 'uppercase', label: 'UPPER' },
  { value: 'none', label: 'As typed' },
  { value: 'capitalize', label: 'Caps' },
];

/** Body section headings on Transformations (not hero): typeface, size, color, bold/italic. */
const TransformationsSectionFontRow = ({ style = {}, onStyleChange, showLineHeight = false, showLetterSpacingAndAlign = false }) => {
  const update = (prop, val) => onStyleChange({ ...style, [prop]: val });
  const showExtras = showLineHeight || showLetterSpacingAndAlign;
  return (
    <div className="mt-1.5 space-y-2">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2 p-2 rounded-md bg-gray-50/80 border border-gray-100" data-testid="transformations-section-font-row">
        <div className="flex flex-col gap-0.5 min-w-[7.5rem]">
          <Label className="text-[10px] text-gray-700 font-medium">Font</Label>
          <select value={style.font_family || ''} onChange={e => update('font_family', e.target.value)} className="text-xs border rounded px-2 py-1.5 bg-white min-w-[7.5rem] max-w-[11rem]" title="Typeface">
            {FONT_OPTIONS.map(f => <option key={f.value || 'default'} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-0.5 min-w-[6.5rem]">
          <Label className="text-[10px] text-gray-700 font-medium">Size</Label>
          <select value={style.font_size || ''} onChange={e => update('font_size', e.target.value)} className="text-xs border rounded px-2 py-1.5 bg-white min-w-[6.5rem] max-w-[12rem]" title="Font size">
            <option value="">Default</option>
            {SECTION_BODY_FONT_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] text-gray-700 font-medium">Color</Label>
          <input type="color" value={style.font_color || '#000000'} onChange={e => update('font_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer border border-gray-200 shrink-0" title="Text color" />
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] text-gray-700 font-medium">Style</Label>
          <div className="flex rounded border border-gray-200 overflow-hidden bg-white h-9">
            <button type="button" onClick={() => update('font_weight', style.font_weight === 'bold' ? '400' : 'bold')} className={`text-xs px-3 ${style.font_weight === 'bold' ? 'bg-gray-800 text-white' : 'bg-white hover:bg-gray-50'}`} title="Bold"><b>B</b></button>
            <button type="button" onClick={() => update('font_style', style.font_style === 'italic' ? 'normal' : 'italic')} className={`text-xs px-3 border-l border-gray-200 ${style.font_style === 'italic' ? 'bg-gray-800 text-white' : 'bg-white hover:bg-gray-50'}`} title="Italic"><i>I</i></button>
          </div>
        </div>
      </div>
      {showExtras && (
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2 p-2 rounded-md bg-gray-50/80 border border-gray-100" data-testid="transformations-section-font-row-extras">
          {showLineHeight && (
            <div className="flex flex-col gap-0.5 min-w-[6.5rem]">
              <Label className="text-[10px] text-gray-700 font-medium">Line height</Label>
              <select value={style.line_height ?? ''} onChange={e => update('line_height', e.target.value)} className="text-xs border rounded px-2 py-1.5 bg-white min-w-[6.5rem]" title="Line height">
                {LINE_HEIGHT_OPTIONS.map(o => <option key={o.value || 'lh-default'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {showLetterSpacingAndAlign && (
            <>
              <div className="flex flex-col gap-0.5 min-w-[6.5rem]">
                <Label className="text-[10px] text-gray-700 font-medium">Letter spacing</Label>
                <select value={style.letter_spacing ?? ''} onChange={e => update('letter_spacing', e.target.value)} className="text-xs border rounded px-2 py-1.5 bg-white min-w-[6.5rem]">
                  {LETTER_SPACING_OPTIONS.map(o => <option key={o.value || 'ls-q'} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-0.5 min-w-[6.5rem]">
                <Label className="text-[10px] text-gray-700 font-medium">Align</Label>
                <select value={style.text_align ?? ''} onChange={e => update('text_align', e.target.value)} className="text-xs border rounded px-2 py-1.5 bg-white min-w-[6.5rem]">
                  {TEXT_ALIGN_OPTIONS.map(o => <option key={o.value || 'ta-q'} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StyleCell = ({ style = {}, onStyleChange, label, wideFontSelect = false, heroExtras = false }) => {
  const update = (prop, val) => onStyleChange({ ...style, [prop]: val });
  return (
    <div className="mt-1">
      {label && <span className="text-[8px] text-gray-400 uppercase tracking-wider">{label}</span>}
      <div className="flex gap-1 items-center flex-wrap">
        <input type="color" value={style.font_color || '#000000'} onChange={e => update('font_color', e.target.value)} className="w-5 h-5 rounded cursor-pointer border-0 shrink-0" title="Color" />
        <select value={style.font_family || ''} onChange={e => update('font_family', e.target.value)} className={`text-[9px] border rounded px-1 py-0.5 ${wideFontSelect ? 'min-w-[7.5rem] max-w-[10rem]' : 'w-16'}`}>
          {FONT_OPTIONS.map(f => <option key={f.value || 'default'} value={f.value}>{f.label}</option>)}
        </select>
        <select value={style.font_size || ''} onChange={e => update('font_size', e.target.value)} className="text-[9px] border rounded px-1 py-0.5 w-12">
          <option value="">Size</option>
          {SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" onClick={() => update('font_weight', style.font_weight === 'bold' ? '400' : 'bold')} className={`text-[9px] px-1 py-0.5 rounded border ${style.font_weight === 'bold' ? 'bg-gray-800 text-white' : 'bg-white'}`}><b>B</b></button>
        <button type="button" onClick={() => update('font_style', style.font_style === 'italic' ? 'normal' : 'italic')} className={`text-[9px] px-1 py-0.5 rounded border ${style.font_style === 'italic' ? 'bg-gray-800 text-white' : 'bg-white'}`}><i>I</i></button>
      </div>
      {heroExtras && (
        <div className="flex gap-1 items-center flex-wrap mt-1">
          <select value={style.letter_spacing ?? ''} onChange={e => update('letter_spacing', e.target.value)} className="text-[9px] border rounded px-1 py-0.5 min-w-[4.5rem]">
            {LETTER_SPACING_OPTIONS.map(o => <option key={o.value || 'ls-default'} value={o.value}>{o.label}</option>)}
          </select>
          <select value={style.text_align ?? ''} onChange={e => update('text_align', e.target.value)} className="text-[9px] border rounded px-1 py-0.5 w-16">
            {TEXT_ALIGN_OPTIONS.map(o => <option key={o.value || 'ta-default'} value={o.value}>{o.label}</option>)}
          </select>
          <select value={style.text_transform ?? ''} onChange={e => update('text_transform', e.target.value)} className="text-[9px] border rounded px-1 py-0.5 w-[4.25rem]">
            {TEXT_TRANSFORM_OPTIONS.map(o => <option key={o.value || 'tt-default'} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

const STATIC_PAGES = [
  { key: 'transformations', label: 'Transformations', defaultTitle: 'TRANSFORMATIONS', defaultSubtitle: 'Stories of Healing, Growth & Awakening', alwaysVisible: true, galleryToggleKey: 'transformations_gallery_visible' },
  { key: 'case_studies', label: 'Case Studies', defaultTitle: 'CASE STUDIES', defaultSubtitle: 'Documented Healing Journeys with Photo Timelines', alwaysVisible: true },
  { key: 'services', label: 'Services', defaultTitle: 'Our Services', defaultSubtitle: 'Claim your personal space', alwaysVisible: true },
  { key: 'contact', label: 'Contact', defaultTitle: 'Express Your Interest', defaultSubtitle: 'Ready to begin your healing journey?', alwaysVisible: true },
  { key: 'about', label: 'About', defaultTitle: 'Dimple Ranawat', defaultSubtitle: 'Founder, Divine Iris – Soulful Healing Studio', alwaysVisible: true },
  { key: 'programs', label: 'All Programs', defaultTitle: 'All Programs', defaultSubtitle: 'Explore our comprehensive healing programs', alwaysVisible: true },
  { key: 'sponsor', label: 'Shine a Light', defaultTitle: 'Shine a Light in a Life', defaultSubtitle: 'Healing flows when we support each other.', alwaysVisible: true },
  { key: 'media', label: 'Media', defaultTitle: 'MEDIA', defaultSubtitle: '', toggleKey: 'media_page_visible' },
  { key: 'blog', label: 'Blog', defaultTitle: 'BLOG', defaultSubtitle: 'Insights, stories and updates', toggleKey: 'blog_page_visible', toggleHint: 'Adds Blog to the header & footer menu and search sitemap. Click Save Changes below.' },
  { key: 'sessions', label: 'Personal Sessions', defaultTitle: 'Personal Healing Sessions', defaultSubtitle: 'Individual sessions tailored to your unique healing journey', toggleKey: 'sessions_page_visible' },
];

const TEMPLATE_STYLE_KEYS = [
  { key: 'title_style', label: 'Hero Title' },
  { key: 'subtitle_style', label: 'Hero Subtitle' },
  { key: 'section_title_style', label: 'Section Titles' },
  { key: 'section_subtitle_style', label: 'Section Subtitles' },
  { key: 'body_style', label: 'Body / Description Text' },
  { key: 'cta_style', label: 'CTA / Button Text' },
  { key: 'pricing_style', label: 'Pricing Font' },
  { key: 'testimonial_title_style', label: 'Testimonials Title' },
];

const DARK_SECTION_STYLE_KEYS = [
  { key: 'exp_title_style', label: 'Title (Dark BG)' },
  { key: 'exp_subtitle_style', label: 'Subtitle (Dark BG)' },
  { key: 'exp_body_style', label: 'Body (Dark BG)' },
];

const SECTION_TYPE_OPTIONS = [
  { value: 'journey', label: 'The Journey', defaultTitle: 'The Journey' },
  { value: 'who_for', label: 'Who It Is For?', defaultTitle: 'Who It Is For?' },
  { value: 'experience', label: 'Your Experience (Dark BG)', defaultTitle: 'Your Experience' },
  { value: 'why_now', label: 'Why You Need This Now?', defaultTitle: 'Why You Need This Now?' },
  { value: 'custom', label: 'Custom Section', defaultTitle: '' },
];

const PageHeadersTab = ({ settings, programs = [], onChange }) => {
  const heroes = settings.page_heroes || {};
  const getHero = (key) => heroes[key] || {};
  const [openSections, setOpenSections] = useState({ pages: true, program_styles: false, program_structure: false, sessions: false });

  const toggle = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const SectionToggle = ({ sectionKey, label, badge, badgeColor = 'purple', count, children }) => {
    const isOpen = openSections[sectionKey];
    return (
      <div className="mt-6">
        <button type="button" onClick={() => toggle(sectionKey)} className="w-full flex items-center gap-2 mb-2 group cursor-pointer" data-testid={`toggle-${sectionKey}`}>
          {isOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
          <p className="text-[10px] font-semibold text-gray-500 group-hover:text-gray-700 transition-colors">{label}</p>
          {badge && <span className={`text-[8px] bg-${badgeColor}-100 text-${badgeColor}-600 px-2 py-0.5 rounded-full font-medium`}>{badge}</span>}
          {count !== undefined && <span className="text-[8px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{count}</span>}
          {!isOpen && <span className="text-[8px] text-gray-400 ml-auto">Click to expand</span>}
        </button>
        {isOpen && children}
      </div>
    );
  };

  const updateHero = (key, field, value) => {
    const updated = { ...heroes, [key]: { ...getHero(key), [field]: value } };
    onChange({ ...settings, page_heroes: updated });
  };

  const applyToAll = () => {
    const src = getHero('home');
    const updated = { ...heroes };
    STATIC_PAGES.forEach(p => {
      updated[p.key] = { ...getHero(p.key), title_style: { ...(src.title_style || {}) }, subtitle_style: { ...(src.subtitle_style || {}) } };
    });
    onChange({ ...settings, page_heroes: updated });
  };

  const [expandedImages, setExpandedImages] = useState({});
  const toggleImage = (key) => setExpandedImages(prev => ({ ...prev, [key]: !prev[key] }));

  const PageRow = ({ pageKey, label, defaultTitle, defaultSubtitle, toggleKey, toggleHint, galleryToggleKey }) => {
    const hero = getHero(pageKey);
    const imgOpen = expandedImages[pageKey] || !!hero.hero_image;
    const isTransformationsHero = pageKey === 'transformations';
    const storiesVisLegacy = isTransformationsHero && hero.stories_body_heading_visible === false && hero.stories_kicker_visible === undefined && hero.stories_title_visible === undefined;
    const videosVisLegacy = isTransformationsHero && hero.videos_body_heading_visible === false && hero.videos_kicker_visible === undefined && hero.videos_title_visible === undefined;
    const galleryVisLegacy = isTransformationsHero && hero.gallery_body_heading_visible === false && hero.gallery_kicker_visible === undefined && hero.gallery_title_visible === undefined;
    return (
      <div className="bg-white rounded-lg border p-3 mb-2" data-testid={`hero-row-${pageKey}`}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
          <span className="text-[10px] font-semibold text-gray-700 w-24 flex-shrink-0">{label}</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ml-auto">
            {galleryToggleKey && (
              <div className="flex items-center gap-1.5" title="Show or hide the Gallery tab and graphic testimonials section on the Transformations page">
                <Label className="text-[9px] text-gray-500 whitespace-nowrap">Gallery visible</Label>
                <Switch checked={settings[galleryToggleKey] !== false} onCheckedChange={v => onChange({ ...settings, [galleryToggleKey]: v })} data-testid="transformations-gallery-visible-switch" />
              </div>
            )}
            {toggleKey && (
              <div className="flex items-center gap-1.5">
                <Label className="text-[9px] text-gray-400">Page visible</Label>
                <Switch checked={settings[toggleKey] || false} onCheckedChange={v => onChange({ ...settings, [toggleKey]: v })} />
              </div>
            )}
          </div>
        </div>
        {toggleHint && (
          <p className="text-[10px] text-amber-700/90 mb-2 leading-relaxed">{toggleHint}</p>
        )}
        {isTransformationsHero && (
          <div className="mb-3 p-3 rounded-lg border border-purple-200 bg-purple-50/80" data-testid="transformations-section-headings">
            <p className="text-[11px] font-semibold text-purple-900 mb-0.5">Transformations page — section titles</p>
            <p className="text-[10px] text-purple-700/80 mb-3">Body section headings only (not the purple hero). Each line has its own <strong>Visible</strong> switch plus font controls. The gold line shows if at least one line is visible. Save Page Headers when done.</p>
            <div className="space-y-4">
              <div className="rounded-md border border-purple-100 bg-white/90 p-2.5">
                <p className="text-[10px] font-semibold text-purple-900 mb-2">Stories section</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-[10px] text-gray-700 font-medium">Small label (kicker)</Label>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label className="text-[9px] text-gray-600 whitespace-nowrap">Visible</Label>
                        <Switch checked={storiesVisLegacy ? false : hero.stories_kicker_visible !== false} onCheckedChange={v => updateHero(pageKey, 'stories_kicker_visible', v)} data-testid="stories-kicker-visible" />
                      </div>
                    </div>
                    <Input value={hero.stories_kicker ?? ''} placeholder={TRANSFORMATIONS_SECTION_DEFAULTS.stories_kicker} onChange={e => updateHero(pageKey, 'stories_kicker', e.target.value)} className="text-xs h-8 mt-1 bg-white" />
                    <TransformationsSectionFontRow style={hero.stories_kicker_style || {}} onStyleChange={v => updateHero(pageKey, 'stories_kicker_style', v)} />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-[10px] text-gray-700 font-medium">Main title</Label>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label className="text-[9px] text-gray-600 whitespace-nowrap">Visible</Label>
                        <Switch checked={storiesVisLegacy ? false : hero.stories_title_visible !== false} onCheckedChange={v => updateHero(pageKey, 'stories_title_visible', v)} data-testid="stories-title-visible" />
                      </div>
                    </div>
                    <Input value={hero.stories_title ?? ''} placeholder={TRANSFORMATIONS_SECTION_DEFAULTS.stories_title} onChange={e => updateHero(pageKey, 'stories_title', e.target.value)} className="text-xs h-8 mt-1 bg-white" />
                    <TransformationsSectionFontRow style={hero.stories_title_style || {}} onStyleChange={v => updateHero(pageKey, 'stories_title_style', v)} />
                  </div>
                  <div className="rounded-md border border-amber-100 bg-amber-50/50 p-2.5" data-testid="transformations-written-quote-style">
                    <p className="text-[10px] font-semibold text-amber-950 mb-0.5">Written cards — client quote</p>
                    <p className="text-[9px] text-amber-900/80 mb-2">Story text on the Transformations page only (grid and popup). Other pages keep their default look.</p>
                    <TransformationsSectionFontRow
                      showLineHeight
                      showLetterSpacingAndAlign
                      style={hero.written_story_quote_style || {}}
                      onStyleChange={v => updateHero(pageKey, 'written_story_quote_style', v)}
                    />
                    <div className="mt-2 flex justify-end">
                      <Button type="button" variant="outline" size="sm" className="text-[10px] h-7"
                        onClick={() => updateHero(pageKey, 'written_story_quote_style', {})}>
                        Reset quote style
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-md border border-purple-100 bg-white/90 p-2.5">
                <p className="text-[10px] font-semibold text-purple-900 mb-2">Videos section</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-[10px] text-gray-700 font-medium">Small label (kicker)</Label>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label className="text-[9px] text-gray-600 whitespace-nowrap">Visible</Label>
                        <Switch checked={videosVisLegacy ? false : hero.videos_kicker_visible !== false} onCheckedChange={v => updateHero(pageKey, 'videos_kicker_visible', v)} data-testid="videos-kicker-visible" />
                      </div>
                    </div>
                    <Input value={hero.video_kicker ?? ''} placeholder={TRANSFORMATIONS_SECTION_DEFAULTS.video_kicker} onChange={e => updateHero(pageKey, 'video_kicker', e.target.value)} className="text-xs h-8 mt-1 bg-white" />
                    <TransformationsSectionFontRow style={hero.video_kicker_style || {}} onStyleChange={v => updateHero(pageKey, 'video_kicker_style', v)} />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-[10px] text-gray-700 font-medium">Main title</Label>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label className="text-[9px] text-gray-600 whitespace-nowrap">Visible</Label>
                        <Switch checked={videosVisLegacy ? false : hero.videos_title_visible !== false} onCheckedChange={v => updateHero(pageKey, 'videos_title_visible', v)} data-testid="videos-title-visible" />
                      </div>
                    </div>
                    <Input value={hero.video_title ?? ''} placeholder={TRANSFORMATIONS_SECTION_DEFAULTS.video_title} onChange={e => updateHero(pageKey, 'video_title', e.target.value)} className="text-xs h-8 mt-1 bg-white" />
                    <TransformationsSectionFontRow style={hero.video_title_style || {}} onStyleChange={v => updateHero(pageKey, 'video_title_style', v)} />
                  </div>
                </div>
              </div>
              <div className="rounded-md border border-purple-100 bg-white/90 p-2.5">
                <p className="text-[10px] font-semibold text-purple-900 mb-2">Gallery section</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-[10px] text-gray-700 font-medium">Small label (kicker)</Label>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label className="text-[9px] text-gray-600 whitespace-nowrap">Visible</Label>
                        <Switch checked={galleryVisLegacy ? false : hero.gallery_kicker_visible !== false} onCheckedChange={v => updateHero(pageKey, 'gallery_kicker_visible', v)} data-testid="gallery-kicker-visible" />
                      </div>
                    </div>
                    <Input value={hero.gallery_kicker ?? ''} placeholder={TRANSFORMATIONS_SECTION_DEFAULTS.gallery_kicker} onChange={e => updateHero(pageKey, 'gallery_kicker', e.target.value)} className="text-xs h-8 mt-1 bg-white" />
                    <TransformationsSectionFontRow style={hero.gallery_kicker_style || {}} onStyleChange={v => updateHero(pageKey, 'gallery_kicker_style', v)} />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-[10px] text-gray-700 font-medium">Main title</Label>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label className="text-[9px] text-gray-600 whitespace-nowrap">Visible</Label>
                        <Switch checked={galleryVisLegacy ? false : hero.gallery_title_visible !== false} onCheckedChange={v => updateHero(pageKey, 'gallery_title_visible', v)} data-testid="gallery-title-visible" />
                      </div>
                    </div>
                    <Input value={hero.gallery_title ?? ''} placeholder={TRANSFORMATIONS_SECTION_DEFAULTS.gallery_title} onChange={e => updateHero(pageKey, 'gallery_title', e.target.value)} className="text-xs h-8 mt-1 bg-white" />
                    <TransformationsSectionFontRow style={hero.gallery_title_style || {}} onStyleChange={v => updateHero(pageKey, 'gallery_title_style', v)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[9px] text-gray-400">Hero Title</Label>
            <Input value={hero.title_text ?? defaultTitle} onChange={e => updateHero(pageKey, 'title_text', e.target.value)} className="text-[10px] h-7 mb-1" />
            <StyleCell style={hero.title_style || {}} onStyleChange={v => updateHero(pageKey, 'title_style', v)} wideFontSelect={isTransformationsHero} heroExtras={isTransformationsHero} />
          </div>
          <div>
            <Label className="text-[9px] text-gray-400">Hero Subtitle</Label>
            <Input value={hero.subtitle_text ?? defaultSubtitle} onChange={e => updateHero(pageKey, 'subtitle_text', e.target.value)} className="text-[10px] h-7 mb-1" />
            <StyleCell style={hero.subtitle_style || {}} onStyleChange={v => updateHero(pageKey, 'subtitle_style', v)} wideFontSelect={isTransformationsHero} heroExtras={isTransformationsHero} />
          </div>
        </div>
        <button type="button" onClick={() => toggleImage(pageKey)} className="mt-2 flex items-center gap-1 text-[9px] text-gray-400 hover:text-gray-600 transition-colors">
          {imgOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          <span>Background Image</span>
          {hero.hero_image && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
        </button>
        {imgOpen && (
          <div className="mt-1.5 flex items-start gap-3">
            <div className="flex-1">
              <ImageUploader value={hero.hero_image || ''} onChange={url => updateHero(pageKey, 'hero_image', url)} />
            </div>
            {hero.hero_image && (
              <div className="flex-shrink-0">
                <img src={resolveImageUrl(hero.hero_image)} alt="Hero BG" className="w-24 h-14 rounded border object-cover" />
                <button type="button" onClick={() => updateHero(pageKey, 'hero_image', '')} className="text-[9px] text-red-400 mt-1 hover:underline">Remove</button>
              </div>
            )}
            <div className="flex-shrink-0">
              <Label className="text-[9px] text-gray-400">Overlay</Label>
              <select value={hero.overlay_opacity ?? '60'} onChange={e => updateHero(pageKey, 'overlay_opacity', e.target.value)} className="text-[9px] border rounded px-1 py-0.5 w-16 block mt-0.5">
                {['0','20','30','40','50','60','70','80','90'].map(v => <option key={v} value={v}>{v}%</option>)}
              </select>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Program Template (font styles)
  const template = getHero('program_template');
  const updateTemplate = (field, value) => updateHero('program_template', field, value);

  // Section Template (structure)
  const sectionTemplate = settings.program_section_template || [];
  const updateSectionTemplate = (newTemplate) => {
    onChange({ ...settings, program_section_template: newTemplate });
  };

  const addSection = () => {
    const newSec = { id: Date.now().toString(), section_type: 'custom', default_title: '', default_subtitle: '', order: sectionTemplate.length, is_enabled: true };
    updateSectionTemplate([...sectionTemplate, newSec]);
  };

  const updateSection = (idx, field, val) => {
    const updated = [...sectionTemplate];
    updated[idx] = { ...updated[idx], [field]: val };
    updateSectionTemplate(updated);
  };

  const removeSection = (idx) => {
    const updated = sectionTemplate.filter((_, i) => i !== idx);
    updated.forEach((s, i) => s.order = i);
    updateSectionTemplate(updated);
  };

  const moveSection = (idx, dir) => {
    const sw = idx + dir;
    if (sw < 0 || sw >= sectionTemplate.length) return;
    const updated = [...sectionTemplate];
    [updated[idx], updated[sw]] = [updated[sw], updated[idx]];
    updated.forEach((s, i) => s.order = i);
    updateSectionTemplate(updated);
  };

  return (
    <div data-testid="page-headers-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Page Headers</h2>
          <p className="text-[10px] text-gray-400">Edit hero title & subtitle for every page. Use "Apply Homepage Style to All" for uniform look.</p>
        </div>
        <Button onClick={applyToAll} variant="outline" size="sm" className="text-[10px] gap-1">
          <Copy size={12} /> Apply Homepage Style to All
        </Button>
      </div>

      <SectionToggle sectionKey="pages" label="STATIC PAGES" count={STATIC_PAGES.length} badge="Hero titles, subtitles & backgrounds">
        {STATIC_PAGES.map(p => (
          <PageRow key={p.key} pageKey={p.key} label={p.label} defaultTitle={p.defaultTitle} defaultSubtitle={p.defaultSubtitle} toggleKey={p.toggleKey} toggleHint={p.toggleHint} galleryToggleKey={p.galleryToggleKey} />
        ))}
      </SectionToggle>

      {/* ===== FLAGSHIP PROGRAM TEMPLATE — FONT STYLES ===== */}
      <SectionToggle sectionKey="program_styles" label="FLAGSHIP PROGRAM PAGES — SHARED STYLES" badge="Applies to all program pages" badgeColor="purple">
      <div className="bg-gradient-to-br from-purple-50/50 to-white rounded-xl border border-purple-100 p-4" data-testid="program-template-section">
        <p className="text-[9px] text-gray-500 mb-3">Font & color changes here apply to <strong>every</strong> program detail page. Set once, used everywhere.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {TEMPLATE_STYLE_KEYS.map(({ key, label }) => (
            <div key={key} className="bg-white rounded-lg border border-gray-100 p-2.5">
              <Label className="text-[9px] text-gray-500 font-semibold block mb-1">{label}</Label>
              <StyleCell style={template[key] || {}} onStyleChange={v => updateTemplate(key, v)} />
            </div>
          ))}
        </div>

        {/* Dark BG Section (Your Experience) styles */}
        <div className="mt-3 mb-3">
          <p className="text-[9px] font-semibold text-gray-500 mb-2">YOUR EXPERIENCE — DARK BACKGROUND SECTION</p>
          <div className="grid grid-cols-3 gap-3">
            {DARK_SECTION_STYLE_KEYS.map(({ key, label }) => (
              <div key={key} className="bg-gray-800 rounded-lg border border-gray-600 p-2.5">
                <Label className="text-[9px] text-yellow-400 font-semibold block mb-1">{label}</Label>
                <StyleCell style={template[key] || {}} onStyleChange={v => updateTemplate(key, v)} />
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Photo (shown on all program pages in the dark section)</Label>
            <div className="flex items-start gap-3">
              <div className="flex-1 max-w-xs">
                <ImageUploader value={template.experience_image || ''} onChange={url => updateTemplate('experience_image', url)} />
              </div>
              {template.experience_image && (
                <div className="flex-shrink-0">
                  <img src={resolveImageUrl(template.experience_image)} alt="Experience" className="w-20 h-20 rounded border border-gray-600 object-cover" />
                  <button type="button" onClick={() => updateTemplate('experience_image', '')} className="text-[9px] text-red-400 mt-1 hover:underline">Remove</button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-[9px] text-gray-500">Hero Background</Label>
            <input type="color" value={template.hero_bg || '#1a1a1a'} onChange={e => updateTemplate('hero_bg', e.target.value)} className="w-6 h-6 rounded cursor-pointer border" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-[9px] text-gray-500">Gold Line Color</Label>
            <input type="color" value={template.accent_color || '#D4AF37'} onChange={e => updateTemplate('accent_color', e.target.value)} className="w-6 h-6 rounded cursor-pointer border" />
          </div>
        </div>
        <div className="mt-3 flex items-start gap-3">
          <div className="flex-1">
            <Label className="text-[9px] text-gray-500 font-semibold">Hero Background Image (all program pages)</Label>
            <ImageUploader value={template.hero_image || ''} onChange={url => updateTemplate('hero_image', url)} />
          </div>
          {template.hero_image && (
            <div className="flex-shrink-0">
              <img src={resolveImageUrl(template.hero_image)} alt="Program Hero" className="w-24 h-14 rounded border object-cover" />
              <button type="button" onClick={() => updateTemplate('hero_image', '')} className="text-[9px] text-red-400 mt-1 hover:underline">Remove</button>
            </div>
          )}
          <div className="flex-shrink-0">
            <Label className="text-[9px] text-gray-500">Overlay</Label>
            <select value={template.overlay_opacity ?? '70'} onChange={e => updateTemplate('overlay_opacity', e.target.value)} className="text-[9px] border rounded px-1 py-0.5 w-16 block mt-0.5">
              {['0','20','30','40','50','60','70','80','90'].map(v => <option key={v} value={v}>{v}%</option>)}
            </select>
          </div>
        </div>

        {/* Gold Line Controls */}
        <div className="mt-4" data-testid="gold-line-controls">
          <p className="text-[9px] font-semibold text-gray-500 mb-2">GOLD LINE SEPARATORS</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'hero_line', label: 'Hero (under subtitle)' },
              { key: 'section_line', label: 'Section Titles' },
              { key: 'exp_line', label: 'Experience (Dark BG)' },
              { key: 'cta_line', label: 'CTA Section' },
            ].map(({ key, label }) => {
              const lineKey = `${key}_visible`;
              const gapKey = `${key}_gap`;
              return (
                <div key={key} className="bg-white rounded-lg border border-gray-100 p-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[9px] text-gray-500">{label}</Label>
                    <Switch checked={template[lineKey] !== false} onCheckedChange={v => updateTemplate(lineKey, v)} />
                  </div>
                  <div className="flex items-center gap-1">
                    <Label className="text-[8px] text-gray-400 whitespace-nowrap">Gap</Label>
                    <select value={template[gapKey] || '10'} onChange={e => updateTemplate(gapKey, e.target.value)} className="text-[9px] border rounded px-1 py-0.5 flex-1">
                      {['2','4','6','8','10','12','16','20','24'].map(v => <option key={v} value={v}>{v}px</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </SectionToggle>

      {/* ===== PROGRAM PAGE SECTIONS — STRUCTURE ===== */}
      <SectionToggle sectionKey="program_structure" label="PROGRAM PAGE SECTIONS — STRUCTURE" badge="Shared across all programs" badgeColor="blue" count={sectionTemplate.length}>
      <div className="bg-gradient-to-br from-blue-50/50 to-white rounded-xl border border-blue-100 p-4" data-testid="section-template-editor">
        <p className="text-[9px] text-gray-500 mb-3">Define which sections appear on every program page and in what order. Add or remove sections here — it applies to <strong>all</strong> programs. Per-program text is edited in the Programs tab.</p>

        <div className="space-y-2">
          {sectionTemplate.map((sec, idx) => {
            const typeInfo = SECTION_TYPE_OPTIONS.find(o => o.value === sec.section_type) || { label: 'Custom' };
            const isDark = sec.section_type === 'experience';
            return (
              <div key={sec.id} data-testid={`template-section-${idx}`} className={`flex items-center gap-2 rounded-lg border p-2.5 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex flex-col gap-0.5">
                  <button type="button" disabled={idx === 0} onClick={() => moveSection(idx, -1)} className="p-0.5 hover:bg-black/10 rounded disabled:opacity-20"><ArrowUp size={10} className={isDark ? 'text-white' : ''} /></button>
                  <button type="button" disabled={idx === sectionTemplate.length - 1} onClick={() => moveSection(idx, 1)} className="p-0.5 hover:bg-black/10 rounded disabled:opacity-20"><ArrowDown size={10} className={isDark ? 'text-white' : ''} /></button>
                </div>
                <span className={`text-[10px] font-bold w-5 ${isDark ? 'text-yellow-400' : 'text-gray-400'}`}>#{idx + 1}</span>
                <select value={sec.section_type} onChange={e => {
                  const newType = e.target.value;
                  const typeOpt = SECTION_TYPE_OPTIONS.find(o => o.value === newType);
                  updateSection(idx, 'section_type', newType);
                  if (!sec.default_title && typeOpt?.defaultTitle) updateSection(idx, 'default_title', typeOpt.defaultTitle);
                }} className={`text-[10px] border rounded px-2 py-1 w-40 ${isDark ? 'bg-gray-700 text-white border-gray-500' : ''}`}>
                  {SECTION_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Input value={sec.default_title || ''} onChange={e => updateSection(idx, 'default_title', e.target.value)} placeholder="Default title..." className={`text-[10px] h-7 flex-1 ${isDark ? 'bg-gray-700 text-white border-gray-500' : ''}`} />
                <Switch checked={sec.is_enabled !== false} onCheckedChange={v => updateSection(idx, 'is_enabled', v)} />
                <button type="button" onClick={() => removeSection(idx)} className={`p-1 rounded hover:bg-black/10 ${isDark ? 'text-red-400' : 'text-red-400'}`}><Trash2 size={12} /></button>
              </div>
            );
          })}
        </div>

        <Button type="button" variant="outline" size="sm" className="mt-3 text-[10px] gap-1" onClick={addSection} data-testid="add-template-section-btn">
          <Plus size={12} /> Add Section
        </Button>
      </div>
      </SectionToggle>

      {/* ===== PERSONAL SESSIONS — COMPLETE STYLE CONTROL ===== */}
      <SectionToggle sectionKey="sessions" label="PERSONAL SESSIONS — STYLE CONTROL" badge="Applies to all session pages" badgeColor="indigo">
      <div className="bg-gradient-to-br from-indigo-50/50 to-white rounded-xl border border-indigo-100 p-4" data-testid="session-template-section">
        {(() => {
          const sessionTpl = heroes['session_template'] || {};
          const updateSessionTpl = (field, value) => updateHero('session_template', field, value);

          return (
            <div className="space-y-5">
              {/* ── HERO SECTION ── */}
              <div className="bg-gradient-to-r from-purple-900 to-indigo-900 rounded-xl p-4 text-white relative overflow-hidden">
                <div className="absolute top-2 right-3 text-[8px] bg-white/20 px-2 py-0.5 rounded-full">Preview: Top banner of every session page</div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-purple-200 mb-3">Hero Section</p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Hero Title Font</Label>
                    <StyleCell style={sessionTpl.hero_title_style || sessionTpl.title_style || {}} onStyleChange={v => updateSessionTpl('hero_title_style', v)} />
                  </div>
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Price Text</Label>
                    <StyleCell style={sessionTpl.hero_price_style || {}} onStyleChange={v => updateSessionTpl('hero_price_style', v)} />
                  </div>
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Badge Style</Label>
                    <div className="flex gap-1 items-center flex-wrap">
                      <Label className="text-[8px] text-purple-300">BG</Label>
                      <input type="color" value={sessionTpl.badge_bg || '#ffffff'} onChange={e => updateSessionTpl('badge_bg', e.target.value)} className="w-5 h-5 rounded cursor-pointer border-0" />
                      <Label className="text-[8px] text-purple-300">Text</Label>
                      <input type="color" value={sessionTpl.badge_text || '#ffffff'} onChange={e => updateSessionTpl('badge_text', e.target.value)} className="w-5 h-5 rounded cursor-pointer border-0" />
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Hero BG Color</Label>
                    <input type="color" value={sessionTpl.hero_bg || '#2a1252'} onChange={e => updateSessionTpl('hero_bg', e.target.value)} className="w-8 h-6 rounded cursor-pointer border border-white/20" />
                  </div>
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Hero Background Image</Label>
                    <ImageUploader value={sessionTpl.hero_image || ''} onChange={url => updateSessionTpl('hero_image', url)} />
                    {sessionTpl.hero_image && (
                      <div className="mt-1 flex items-center gap-2">
                        <img src={resolveImageUrl(sessionTpl.hero_image)} alt="Sessions Hero" className="w-16 h-10 rounded border border-white/20 object-cover" />
                        <button type="button" onClick={() => updateSessionTpl('hero_image', '')} className="text-[9px] text-red-300 hover:underline">Remove</button>
                      </div>
                    )}
                  </div>
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Purple Intensity</Label>
                    <select value={sessionTpl.page_purple || 'medium'} onChange={e => updateSessionTpl('page_purple', e.target.value)} className="text-[10px] border rounded px-2 py-1 w-full text-gray-800">
                      <option value="light">Light</option>
                      <option value="medium">Medium</option>
                      <option value="strong">Strong</option>
                    </select>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Accent / Line Color</Label>
                    <input type="color" value={sessionTpl.accent_color || '#D4AF37'} onChange={e => updateSessionTpl('accent_color', e.target.value)} className="w-8 h-6 rounded cursor-pointer border border-white/20" />
                  </div>
                  <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm">
                    <Label className="text-[9px] text-purple-200 font-semibold block mb-1">Star Color</Label>
                    <input type="color" value={sessionTpl.star_color || '#D4AF37'} onChange={e => updateSessionTpl('star_color', e.target.value)} className="w-8 h-6 rounded cursor-pointer border border-white/20" />
                  </div>
                </div>
              </div>

              {/* ── BODY CONTENT ── */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 relative">
                <div className="absolute top-2 right-3 text-[8px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Preview: Content below hero on session page</div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">Body Content</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Section Title ("About...")</Label>
                    <StyleCell style={sessionTpl.title_style || {}} onStyleChange={v => updateSessionTpl('title_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Description Text</Label>
                    <StyleCell style={sessionTpl.description_style || {}} onStyleChange={v => updateSessionTpl('description_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">"What Clients Say" Heading</Label>
                    <StyleCell style={sessionTpl.testimonial_heading_style || {}} onStyleChange={v => updateSessionTpl('testimonial_heading_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Testimonial Text</Label>
                    <StyleCell style={sessionTpl.testimonial_style || {}} onStyleChange={v => updateSessionTpl('testimonial_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Info Card Heading</Label>
                    <StyleCell style={sessionTpl.info_card_heading_style || {}} onStyleChange={v => updateSessionTpl('info_card_heading_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Info Card Bullet Text</Label>
                    <StyleCell style={sessionTpl.info_card_text_style || {}} onStyleChange={v => updateSessionTpl('info_card_text_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">"Ask a Question" Heading</Label>
                    <StyleCell style={sessionTpl.question_heading_style || {}} onStyleChange={v => updateSessionTpl('question_heading_style', v)} />
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Question Form Labels</Label>
                    <StyleCell style={sessionTpl.question_label_style || {}} onStyleChange={v => updateSessionTpl('question_label_style', v)} />
                  </div>
                </div>
                <div className="mt-3 bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                  <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Question Form Intro Text</Label>
                  <Input value={sessionTpl.question_intro_text || "Have a question? We'll respond within 7 working days."} onChange={e => updateSessionTpl('question_intro_text', e.target.value)} className="text-[10px] h-7" placeholder="Have a question? We'll respond within 7 working days." />
                </div>

                {/* Editable Info Cards */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-purple-50/40 rounded-lg border border-purple-100 p-3">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Card 1 Title</Label>
                    <Input value={sessionTpl.card1_title || 'What to Expect'} onChange={e => updateSessionTpl('card1_title', e.target.value)} className="text-[10px] h-7 mb-2" placeholder="What to Expect" />
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Bullet Points (one per line)</Label>
                    <textarea value={(sessionTpl.card1_items || ['Personalized healing approach','Safe and supportive environment','Immediate energetic shifts','Practical guidance for integration']).join('\n')}
                      onChange={e => updateSessionTpl('card1_items', e.target.value.split('\n'))}
                      className="w-full text-[10px] border rounded px-2 py-1.5 resize-none" rows={4} placeholder="One bullet per line" />
                  </div>
                  <div className="bg-purple-50/40 rounded-lg border border-purple-100 p-3">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Card 2 Title</Label>
                    <Input value={sessionTpl.card2_title || 'Who Is This For'} onChange={e => updateSessionTpl('card2_title', e.target.value)} className="text-[10px] h-7 mb-2" placeholder="Who Is This For" />
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Bullet Points (one per line)</Label>
                    <textarea value={(sessionTpl.card2_items || ['Anyone seeking deep healing','Those ready for transformation','Individuals committed to growth','Open to energetic work']).join('\n')}
                      onChange={e => updateSessionTpl('card2_items', e.target.value.split('\n'))}
                      className="w-full text-[10px] border rounded px-2 py-1.5 resize-none" rows={4} placeholder="One bullet per line" />
                  </div>
                </div>

                {/* Footer Note */}
                <div className="mt-3 bg-gray-50 rounded-lg border border-gray-100 p-2.5">
                  <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Booking Footer Note (below calendar)</Label>
                  <Input value={sessionTpl.footer_note || 'Sessions conducted online via Zoom or in-person by appointment. Each session is customized to your unique healing needs.'} onChange={e => updateSessionTpl('footer_note', e.target.value)} className="text-[10px] h-7" placeholder="Sessions conducted online via Zoom..." />
                </div>
              </div>

              {/* ── BUTTONS & CALENDAR ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 relative">
                  <div className="absolute top-2 right-3 text-[8px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Preview: "Book Now" & "View Details" buttons</div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">Buttons</p>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[9px] text-gray-500">Primary Button Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <input type="color" value={sessionTpl.button_bg || '#D4AF37'} onChange={e => updateSessionTpl('button_bg', e.target.value)} className="w-8 h-6 rounded cursor-pointer border" />
                        <span className="text-[9px] text-gray-400">{sessionTpl.button_bg || '#D4AF37'}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Button Text Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <input type="color" value={sessionTpl.button_text || '#1a1a1a'} onChange={e => updateSessionTpl('button_text', e.target.value)} className="w-8 h-6 rounded cursor-pointer border" />
                        <span className="text-[9px] text-gray-400">{sessionTpl.button_text || '#1a1a1a'}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Quick Presets</Label>
                      <div className="flex gap-1.5 mt-1">
                        {[
                          { label: 'Gold', bg: '#D4AF37', text: '#1a1a1a' },
                          { label: 'Coal Black', bg: '#1a1a1a', text: '#D4AF37' },
                          { label: 'Purple', bg: '#7c3aed', text: '#ffffff' },
                          { label: 'Deep Iris', bg: '#4c1d95', text: '#D4AF37' },
                        ].map(preset => (
                          <button key={preset.label} type="button"
                            onClick={() => { updateSessionTpl('button_bg', preset.bg); updateSessionTpl('button_text', preset.text); }}
                            className="text-[8px] px-2 py-1 rounded-full border border-gray-200 hover:border-gray-400 transition-colors"
                            style={{ background: preset.bg, color: preset.text }}>
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── GLOBAL OFFER ── */}
                <div className="md:col-span-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4 relative">
                  <div className="absolute top-2 right-3 text-[8px] bg-green-100 text-green-600 px-2 py-0.5 rounded-full">Preview: Offer badge on ALL sessions</div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-green-700 mb-3">Global Special Offer</p>
                  <p className="text-[9px] text-green-600 mb-3">Applies an offer badge to all sessions. Per-session offers are set in each session's edit form.</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-[9px] text-gray-500">Offer Badge Text</Label>
                      <Input value={sessionTpl.global_offer_text || ''} onChange={e => updateSessionTpl('global_offer_text', e.target.value)} className="text-[10px] h-7 bg-white" placeholder="e.g., Limited Time Offer" />
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Badge Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <input type="color" value={sessionTpl.offer_badge_bg || '#ef4444'} onChange={e => updateSessionTpl('offer_badge_bg', e.target.value)} className="w-6 h-5 rounded cursor-pointer border" />
                        <input type="color" value={sessionTpl.offer_badge_text || '#ffffff'} onChange={e => updateSessionTpl('offer_badge_text', e.target.value)} className="w-6 h-5 rounded cursor-pointer border" />
                        <span className="text-[8px] text-gray-400">BG / Text</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Offer Expiry Date</Label>
                      <Input type="date" value={sessionTpl.global_offer_expiry || ''} onChange={e => updateSessionTpl('global_offer_expiry', e.target.value)} className="text-[10px] h-7 bg-white" />
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Quick Badge Presets</Label>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {['Limited Time Offer', 'Early Bird Special', 'Festive Discount', '50% OFF'].map(t => (
                          <button key={t} type="button" onClick={() => updateSessionTpl('global_offer_text', t)}
                            className="text-[7px] px-1.5 py-0.5 rounded-full bg-white border border-green-200 hover:border-green-400 text-green-700">{t}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 relative">
                  <div className="absolute top-2 right-3 text-[8px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Preview: Booking calendar on session pages</div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">Calendar & Booking</p>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[9px] text-gray-500">"Book Your Session" Heading</Label>
                      <StyleCell style={sessionTpl.calendar_heading_style || {}} onStyleChange={v => updateSessionTpl('calendar_heading_style', v)} />
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Calendar Accent Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <input type="color" value={sessionTpl.calendar_accent || '#D4AF37'} onChange={e => updateSessionTpl('calendar_accent', e.target.value)} className="w-8 h-6 rounded cursor-pointer border" />
                        <span className="text-[9px] text-gray-400">Selected date & highlights</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Calendar BG Color</Label>
                      <div className="flex gap-2 items-center mt-1">
                        <input type="color" value={sessionTpl.calendar_bg || '#2a1252'} onChange={e => updateSessionTpl('calendar_bg', e.target.value)} className="w-8 h-6 rounded cursor-pointer border" />
                        <span className="text-[9px] text-gray-400">Booking sidebar background</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[9px] text-gray-500">Homepage Purple Intensity</Label>
                      <select value={sessionTpl.homepage_purple || 'medium'} onChange={e => updateSessionTpl('homepage_purple', e.target.value)} className="text-[10px] border rounded px-2 py-1 w-full mt-1">
                        <option value="light">Light — Subtle lavender</option>
                        <option value="medium">Medium — Balanced iris</option>
                        <option value="strong">Strong — Deep violet</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── HOMEPAGE LIST ── */}
              <div className="bg-purple-50/50 rounded-xl border border-purple-100 p-4 relative">
                <div className="absolute top-2 right-3 text-[8px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">Preview: Session names list on homepage</div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">Homepage Session List</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Session Name Font</Label>
                    <StyleCell style={sessionTpl.title_style || {}} onStyleChange={v => updateSessionTpl('title_style', v)} />
                  </div>
                  <div className="bg-white rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Price Style</Label>
                    <StyleCell style={sessionTpl.price_style || {}} onStyleChange={v => updateSessionTpl('price_style', v)} />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Placeholder Title ("Choose Your...")</Label>
                    <Input value={sessionTpl.placeholder_title || 'Choose Your Healing Journey'} onChange={e => updateSessionTpl('placeholder_title', e.target.value)} className="text-[10px] h-7 mb-1" />
                    <StyleCell style={sessionTpl.placeholder_title_style || {}} onStyleChange={v => updateSessionTpl('placeholder_title_style', v)} />
                  </div>
                  <div className="bg-white rounded-lg border border-gray-100 p-2.5">
                    <Label className="text-[9px] text-gray-500 font-semibold block mb-1">Placeholder Subtitle</Label>
                    <Input value={sessionTpl.placeholder_subtitle || 'Select a session from the left to explore details, read testimonials, and book your personal appointment.'} onChange={e => updateSessionTpl('placeholder_subtitle', e.target.value)} className="text-[10px] h-7 mb-1" />
                    <StyleCell style={sessionTpl.placeholder_subtitle_style || {}} onStyleChange={v => updateSessionTpl('placeholder_subtitle_style', v)} />
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      </SectionToggle>
    </div>
  );
};

export default PageHeadersTab;
